(() => {
  'use strict';

  const configElement = document.getElementById(
    'bslabs-gift-config'
  );

  if (!configElement) {
    console.warn(
      'BSLABS: configurazione omaggi non trovata.'
    );
    return;
  }

  let config;

  try {
    config = JSON.parse(configElement.textContent);
    window.BSLABS_GIFTS = config;
  } catch (error) {
    console.error(
      'BSLABS: configurazione omaggi non valida.',
      error
    );
    return;
  }

  const root = window.Shopify?.routes?.root || '/';
  const nativeFetch = window.fetch.bind(window);

  let giftOperationInProgress = false;
  let promoSyncInProgress = false;
  let promoRefreshTimer = null;
  let checkoutInProgress = false;

  function wait(milliseconds) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, milliseconds);
    });
  }

  function reloadCartPage(delay = 300) {
    window.setTimeout(() => {
      window.location.reload();
    }, delay);
  }

  function normalizeCode(value) {
    return String(value || '')
      .trim()
      .toUpperCase();
  }

  function isSupportedAthleteCode(value) {
    const code = normalizeCode(value);
    return code === 'ROSANOFIGHT' || code.endsWith('FIT');
  }

  function getGiftVariantIds() {
    return new Set(
      Object.values(config.gifts)
        .flatMap((gift) => gift.variants)
        .map((variant) => Number(variant.id))
    );
  }

  function getGiftTypeByVariantId(variantId) {
    const numericVariantId = Number(variantId);

    for (
      const [giftType, gift] of
      Object.entries(config.gifts)
    ) {
      const exists = gift.variants.some(
        (variant) => {
          return (
            Number(variant.id) ===
            numericVariantId
          );
        }
      );

      if (exists) {
        return giftType;
      }
    }

    return '';
  }

  function getAvailableGiftVariants(giftType) {
    const gift = config.gifts[giftType];

    if (!gift) {
      return [];
    }

    return gift.variants.filter((variant) => {
      return variant.available === true;
    });
  }

  async function readCart() {
    const response = await nativeFetch(
      `${root}cart.js`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(
        `Errore lettura carrello: ${response.status}`
      );
    }

    return response.json();
  }

  async function postCartUpdate(payload) {
    const response = await nativeFetch(
      `${root}cart/update.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => null);

      throw new Error(
        errorData?.description ||
        `Errore aggiornamento carrello: ${response.status}`
      );
    }

    return response.json();
  }

  async function addGiftVariant(
    variantId,
    tierLevel
  ) {
    const response = await nativeFetch(
      `${root}cart/add.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                _bslabs_gift: 'true',
                _bslabs_gift_tier:
                  String(tierLevel)
              }
            }
          ]
        })
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => null);

      throw new Error(
        errorData?.description ||
        'Omaggio non disponibile.'
      );
    }

    return response.json();
  }

  function getGiftLines(cart) {
    const giftVariantIds = getGiftVariantIds();

    return cart.items.filter((item) => {
      const isAutomaticGift =
        String(item.properties?._bslabs_gift || '').toLowerCase() === 'true';

      return (
        isAutomaticGift &&
        giftVariantIds.has(Number(item.variant_id))
      );
    });
  }


  function getStoredAthleteCode(cart) {
    return normalizeCode(
      cart.attributes?.bslabs_athlete_code
    );
  }

  function uniqueDiscountCodes(codes) {
    const seen = new Set();
    return codes
      .map((code) => normalizeCode(code))
      .filter(Boolean)
      .filter((code) => {
        if (seen.has(code)) return false;
        seen.add(code);
        return true;
      });
  }

  function getCartDiscountCodeStates(cart) {
    const states = new Map();

    const setState = (codeValue, applicable) => {
      const code = normalizeCode(codeValue);
      if (!code) return;
      const previous = states.get(code);
      states.set(code, {
        code,
        applicable:
          applicable === true ||
          previous?.applicable === true
      });
    };

    if (Array.isArray(cart?.discount_codes)) {
      for (const discountCode of cart.discount_codes) {
        setState(
          discountCode?.code,
          discountCode?.applicable
        );
      }
    }

    const collectApplication = (application) => {
      if (!application) return;

      const applicationType =
        normalizeCode(application.type);

      if (
        applicationType &&
        applicationType !== 'DISCOUNT_CODE'
      ) {
        return;
      }

      setState(application.title, true);
    };

    (
      cart?.cart_level_discount_applications || []
    ).forEach(collectApplication);

    (cart?.items || []).forEach((item) => {
      (
        item?.line_level_discount_allocations || []
      ).forEach((allocation) => {
        collectApplication(
          allocation?.discount_application
        );
      });
    });

    return Array.from(states.values());
  }

  function getCartDiscountCodes(cart) {
    return uniqueDiscountCodes(
      getCartDiscountCodeStates(cart)
        .map((discountCode) => discountCode.code)
    );
  }

  function isDiscountCodeApplicable(cart, code) {
    const normalizedCode = normalizeCode(code);

    return getCartDiscountCodeStates(cart)
      .some((discountCode) => {
        return (
          discountCode.code === normalizedCode &&
          discountCode.applicable === true
        );
      });
  }

  function getAppliedAthleteCode(cart) {
    const discountCode =
      getCartDiscountCodeStates(cart)
        .find((candidate) => {
          return (
            candidate.applicable === true &&
            isSupportedAthleteCode(candidate.code)
          );
        });

    return discountCode?.code || '';
  }

  function serializeDiscountCodes(codes) {
    return uniqueDiscountCodes(codes).join(',');
  }

  function getDisplacedApplicableCodes(
    cartBefore,
    cartAfter,
    ignoredCode = ''
  ) {
    const ignored = normalizeCode(ignoredCode);

    return getCartDiscountCodeStates(cartBefore)
      .filter((discountCode) => {
        return (
          discountCode.applicable === true &&
          discountCode.code !== ignored
        );
      })
      .map((discountCode) => discountCode.code)
      .filter((code) => {
        return !isDiscountCodeApplicable(
          cartAfter,
          code
        );
      });
  }

  async function restoreDiscountState(
    previousCodes,
    previousAthleteCode
  ) {
    await postCartUpdate({
      discount: serializeDiscountCodes(
        previousCodes
      ),
      attributes: {
        bslabs_athlete_code:
          previousAthleteCode || ''
      }
    });

    return readCart();
  }

  async function synchronizeAthleteCodeAttribute(
    cart
  ) {
    const activeCode =
      getAppliedAthleteCode(cart);

    const storedCode =
      getStoredAthleteCode(cart);

    if (activeCode === storedCode) {
      return { cart, activeCode };
    }

    const updatedCart =
      await postCartUpdate({
        attributes: {
          bslabs_athlete_code: activeCode
        }
      });

    return {
      cart: updatedCart,
      activeCode
    };
  }

  function getQualifyingSubtotal(
    cart,
    activeCode = ''
  ) {
    const giftVariantIds =
      getGiftVariantIds();

    const subtotalBeforeAthleteDiscount =
      cart.items.reduce(
        (subtotal, item) => {
          if (
            giftVariantIds.has(
              Number(item.variant_id)
            )
          ) {
            return subtotal;
          }

          return subtotal + Number(
            item.final_line_price ??
            item.line_price ??
            0
          );
        },
        0
      );

    if (!activeCode) {
      return subtotalBeforeAthleteDiscount;
    }

    const discountPercent = Number(
      config.athleteDiscountPercent || 0
    );

    const multiplier = Math.max(
      0,
      1 - discountPercent / 100
    );

    return Math.round(
      subtotalBeforeAthleteDiscount * multiplier
    );
  }
  function getGiftTier(_subtotal) {
    // V2: the gift thresholds have no matching active Shopify discount rule to verify.
    // Keep athlete-code handling intact and do not add or promise unconfirmed gifts.
    return null;
  }

  window.BSLABS_GIFTS_API = Object.freeze({
    getQualifyingSubtotal,
    getGiftTier
  });

  function buildGiftRemovalUpdates(cart) {
    const updates = {};

    for (
      const line of
      getGiftLines(cart)
    ) {
      updates[String(line.key)] = 0;
    }

    return updates;
  }

  function hasUpdates(updates) {
    return (
      Object.keys(updates).length > 0
    );
  }

  async function removeAllGiftLines(cart) {
    const updates =
      buildGiftRemovalUpdates(cart);

    if (!hasUpdates(updates)) {
      return false;
    }

    await postCartUpdate({
      updates
    });

    return true;
  }

  /*
   * Controlla che nel carrello ci sia
   * soltanto un omaggio valido.
   *
   * Se il totale scende:
   * - elimina omaggi non più consentiti;
   * - limita la quantità a uno;
   * - sotto 100 € elimina tutti gli omaggi.
   *
   * Nella fascia 100 € lo Shaker viene
   * aggiunto automaticamente.
   */
  async function normalizeGiftLines(
    cart,
    activeCode,
    tier
  ) {
    const giftLines =
      getGiftLines(cart);

    if (!activeCode || !tier) {
      return removeAllGiftLines(cart);
    }

    const validLines =
      giftLines.filter((line) => {
        const giftType =
          getGiftTypeByVariantId(
            line.variant_id
          );

        return tier.allowedGifts.includes(
          giftType
        );
      });

    const lineToKeep =
      validLines[0] || null;

    const updates = {};

    for (const line of giftLines) {
      const keepThisLine =
        lineToKeep &&
        String(line.key) ===
        String(lineToKeep.key);

      if (!keepThisLine) {
        updates[String(line.key)] = 0;
        continue;
      }

      if (
        Number(line.quantity) !== 1
      ) {
        updates[String(line.key)] = 1;
      }
    }

    if (hasUpdates(updates)) {
      await postCartUpdate({
        updates
      });

      return true;
    }

    if (
      tier.level === 100 &&
      !lineToKeep
    ) {
      const shakerVariant =
        getAvailableGiftVariants(
          'shaker'
        )[0];

      if (!shakerVariant) {
        setGiftMessage(
          'Lo Shaker omaggio è momentaneamente esaurito.',
          'error'
        );

        return false;
      }

      await addGiftVariant(
        shakerVariant.id,
        tier.level
      );

      return true;
    }

    return false;
  }


  function getAthleteElements() {
    return {
      container:
        document.getElementById(
          'BslabsAthleteDiscount'
        ),

      input:
        document.getElementById(
          'BslabsAthleteCode'
        ),

      applyButton:
        document.getElementById(
          'BslabsApplyAthleteCode'
        ),

      removeButton:
        document.getElementById(
          'BslabsRemoveAthleteCode'
        ),

      message:
        document.getElementById(
          'BslabsAthleteCodeMessage'
        ),
      entry:
        document.getElementById('BslabsAthleteCodeEntry'),
      title:
        document.getElementById('BslabsAthleteCodeTitle'),
      subtitle:
        document.getElementById('BslabsAthleteCodeSubtitle')
    };
  }
  function setAthleteMessage(
    text,
    status = ''
  ) {
    const { message } =
      getAthleteElements();

    if (!message) {
      return;
    }

    message.textContent = text;

    if (status) {
      message.dataset.status = status;
    } else {
      delete message.dataset.status;
    }
  }
  function setAthleteLoading(
    isLoading
  ) {
    const {
      input,
      applyButton,
      removeButton
    } = getAthleteElements();

    if (input) {
      input.disabled = isLoading;
    }

    if (applyButton) {
      applyButton.disabled =
        isLoading;

      applyButton.textContent =
        isLoading
          ? 'Attendi...'
          : 'Applica';
    }

    if (removeButton) {
      removeButton.disabled =
        isLoading;
    }
  }
  function displayActiveAthleteCode(
    code
  ) {
    const {
      input,
      removeButton
    } = getAthleteElements();

    if (input) {
      input.value = code;
    }

    const { entry, title, subtitle } = getAthleteElements();
    if (entry) entry.hidden = true;
    if (title) title.textContent = 'Codice atleta attivo';
    if (subtitle) subtitle.hidden = true;

    if (removeButton) {
      removeButton.hidden = false;
    }

    setAthleteMessage(
      `${code} · 5% di sconto attivo.`,
      'success'
    );
  }
  function displayNoAthleteCode() {
    const { removeButton, entry, title, subtitle } =
      getAthleteElements();

    if (removeButton) {
      removeButton.hidden = true;
    }
    if (entry) entry.hidden = false;
    if (title) title.textContent = 'Hai un codice atleta?';
    if (subtitle) subtitle.hidden = false;
  }
  function cartContainsDiscountCode(cart, code) {
    return isDiscountCodeApplicable(cart, code);
  }


  async function applyAthleteCode() {
    const { input } = getAthleteElements();
    if (!input) return;

    const code = normalizeCode(input.value);
    input.value = code;

    if (!code) {
      setAthleteMessage('Inserisci un codice atleta.', 'error');
      return;
    }

    if (!isSupportedAthleteCode(code)) {
      setAthleteMessage(
        'Codice atleta non riconosciuto.',
        'error'
      );
      return;
    }

    setAthleteLoading(true);
    setAthleteMessage('Verifica del codice in corso...');

    let previousCodes = null;
    let previousAthleteCode = '';
    let discountMutationStarted = false;

    try {
      const cartBefore = await readCart();
      previousCodes = getCartDiscountCodes(cartBefore);
      previousAthleteCode = getAppliedAthleteCode(cartBefore);

      if (
        previousAthleteCode &&
        previousAthleteCode !== code
      ) {
        setAthleteMessage(
          `È già attivo il codice atleta ${previousAthleteCode}. Rimuovilo prima di applicarne un altro.`,
          'error'
        );
        return;
      }

      if (
        previousAthleteCode === code &&
        isDiscountCodeApplicable(cartBefore, code)
      ) {
        await postCartUpdate({
          attributes: { bslabs_athlete_code: code }
        });
        displayActiveAthleteCode(code);
        await syncGifts();
        return;
      }

      const candidateCodes =
        uniqueDiscountCodes([...previousCodes, code]);

      discountMutationStarted = true;

      await postCartUpdate({
        discount: serializeDiscountCodes(candidateCodes)
      });

      const cartAfterDiscount = await readCart();

      const athleteIsApplicable =
        isDiscountCodeApplicable(cartAfterDiscount, code);

      const displacedCodes =
        getDisplacedApplicableCodes(
          cartBefore,
          cartAfterDiscount,
          code
        );

      if (
        !athleteIsApplicable ||
        displacedCodes.length > 0
      ) {
        await restoreDiscountState(
          previousCodes,
          previousAthleteCode
        );
        discountMutationStarted = false;

        if (previousAthleteCode) {
          displayActiveAthleteCode(previousAthleteCode);
        } else {
          displayNoAthleteCode();
        }

        if (displacedCodes.length > 0) {
          setAthleteMessage(
            `Il codice atleta non è combinabile con ${displacedCodes.join(', ')}. Gli sconti già presenti sono stati mantenuti.`,
            'error'
          );
        } else if (previousCodes.length > 0) {
          setAthleteMessage(
            'Il codice atleta non è applicabile insieme agli sconti già presenti oppure non soddisfa le condizioni del carrello. Gli sconti precedenti sono stati mantenuti.',
            'error'
          );
        } else {
          setAthleteMessage(
            'Codice non valido, scaduto o non applicabile al carrello.',
            'error'
          );
        }
        return;
      }

      await postCartUpdate({
        attributes: { bslabs_athlete_code: code }
      });
      discountMutationStarted = false;

      displayActiveAthleteCode(code);
      reloadCartPage(400);
    } catch (error) {
      console.error(
        'BSLABS: errore applicazione codice',
        error
      );

      let rollbackSucceeded = false;
      if (
        discountMutationStarted &&
        Array.isArray(previousCodes)
      ) {
        try {
          await restoreDiscountState(
            previousCodes,
            previousAthleteCode
          );
          rollbackSucceeded = true;
        } catch (rollbackError) {
          console.error(
            'BSLABS: errore ripristino codici',
            rollbackError
          );
        }
      }

      if (previousAthleteCode) {
        displayActiveAthleteCode(previousAthleteCode);
      } else {
        displayNoAthleteCode();
      }

      setAthleteMessage(
        rollbackSucceeded
          ? 'Non è stato possibile applicare il codice atleta. Gli sconti precedenti sono stati ripristinati.'
          : 'Non è stato possibile applicare il codice atleta. Controlla gli sconti presenti nel carrello.',
        'error'
      );
    } finally {
      setAthleteLoading(false);
    }
  }
  async function removeAthleteCode() {
    setAthleteLoading(true);
    setAthleteMessage(
      'Rimozione del codice in corso...'
    );

    let previousCodes = null;
    let activeAthleteCode = '';
    let discountMutationStarted = false;

    try {
      const cartBefore = await readCart();

      activeAthleteCode =
        getAppliedAthleteCode(cartBefore);

      previousCodes =
        getCartDiscountCodes(cartBefore);

      if (!activeAthleteCode) {
        await postCartUpdate({
          attributes: {
            bslabs_athlete_code: ''
          }
        });

        const latestCart =
          await readCart();

        try {
          await removeAllGiftLines(latestCart);
        } catch (giftError) {
          console.error(
            'BSLABS: errore pulizia omaggi',
            giftError
          );
        }

        displayNoAthleteCode();
        hideGiftSelector();
        reloadCartPage(300);
        return;
      }

      const remainingCodes =
        previousCodes.filter((code) => {
          return code !== activeAthleteCode;
        });

      discountMutationStarted = true;

      await postCartUpdate({
        discount:
          serializeDiscountCodes(remainingCodes),
        attributes: {
          bslabs_athlete_code: ''
        }
      });

      const cartAfterDiscountRemoval =
        await readCart();

      const athleteStillApplied =
        isDiscountCodeApplicable(
          cartAfterDiscountRemoval,
          activeAthleteCode
        );

      const displacedCodes =
        getDisplacedApplicableCodes(
          cartBefore,
          cartAfterDiscountRemoval,
          activeAthleteCode
        );

      if (
        athleteStillApplied ||
        displacedCodes.length > 0
      ) {
        await restoreDiscountState(
          previousCodes,
          activeAthleteCode
        );

        discountMutationStarted = false;

        displayActiveAthleteCode(
          activeAthleteCode
        );

        setAthleteMessage(
          'Non è stato possibile rimuovere il solo codice atleta senza alterare gli altri sconti. Lo stato precedente è stato ripristinato.',
          'error'
        );
        return;
      }

      discountMutationStarted = false;

      try {
        await removeAllGiftLines(
          cartAfterDiscountRemoval
        );
      } catch (giftError) {
        console.error(
          'BSLABS: errore pulizia omaggi',
          giftError
        );
      }

      displayNoAthleteCode();
      hideGiftSelector();
      reloadCartPage(300);
    } catch (error) {
      console.error(
        'BSLABS: errore rimozione codice',
        error
      );

      let rollbackSucceeded = false;

      if (
        discountMutationStarted &&
        Array.isArray(previousCodes)
      ) {
        try {
          await restoreDiscountState(
            previousCodes,
            activeAthleteCode
          );
          rollbackSucceeded = true;
        } catch (rollbackError) {
          console.error(
            'BSLABS: errore ripristino codici',
            rollbackError
          );
        }
      }

      if (activeAthleteCode) {
        displayActiveAthleteCode(
          activeAthleteCode
        );
      }

      setAthleteMessage(
        rollbackSucceeded
          ? 'Non è stato possibile rimuovere il codice atleta. Gli sconti precedenti sono stati ripristinati.'
          : 'Non è stato possibile rimuovere il codice atleta. Controlla gli sconti presenti nel carrello.',
        'error'
      );
    } finally {
      setAthleteLoading(false);
    }
  }

  function getGiftElements() {
    return {
      selector:
        document.getElementById(
          'BslabsGiftSelector'
        ),

      subtitle:
        document.getElementById(
          'BslabsGiftSelectorSubtitle'
        ),

      options:
        document.getElementById(
          'BslabsGiftOptions'
        ),

      message:
        document.getElementById(
          'BslabsGiftSelectorMessage'
        )
    };
  }
  function setGiftMessage(
    text,
    status = ''
  ) {
    const { message } =
      getGiftElements();

    if (!message) {
      return;
    }

    message.textContent = text;

    if (status) {
      message.dataset.status =
        status;
    } else {
      delete message.dataset.status;
    }
  }
  function hideGiftSelector() {
    const {
      selector,
      options
    } = getGiftElements();

    if (selector) {
      selector.hidden = true;
    }

    if (options) {
      options.innerHTML = '';
    }

    setGiftMessage('');
  }
  function getTierDescription(tier) {
    if (tier.level === 100) {
      return (
        'Hai raggiunto 100 € netti: ' +
        'lo Shaker viene aggiunto automaticamente.'
      );
    }

    if (tier.level === 130) {
      return (
        'Hai raggiunto 130 € netti: ' +
        'scegli tra Shaker, Telo e Sacca.'
      );
    }

    return (
      'Hai raggiunto 180 € netti: ' +
      'scegli tra Shaker, Telo, Sacca e T-shirt.'
    );
  }

  function setGiftButtonsLoading(
    isLoading
  ) {
    document
      .querySelectorAll(
        '.bslabs-gift-option__button'
      )
      .forEach((button) => {
        button.disabled =
          isLoading;
      });

    document
      .querySelectorAll(
        '.bslabs-gift-option__size'
      )
      .forEach((select) => {
        select.disabled =
          isLoading;
      });
  }

  function createGiftOption(
    giftType,
    currentGiftLine,
    tier
  ) {
    const gift =
      config.gifts[giftType];

    const availableVariants =
      getAvailableGiftVariants(
        giftType
      );

    const row =
      document.createElement('div');

    row.className =
      'bslabs-gift-option';

    const currentGiftType =
      currentGiftLine
        ? getGiftTypeByVariantId(
            currentGiftLine
              .variant_id
          )
        : '';

    const isSelected =
      currentGiftType === giftType;

    if (isSelected) {
      row.classList.add(
        'is-selected'
      );
    }

    const name =
      document.createElement('div');

    name.className =
      'bslabs-gift-option__name';

    name.textContent =
      gift.title;

    const controls =
      document.createElement('div');

    controls.className =
      'bslabs-gift-option__controls';

    let sizeSelect = null;

    if (giftType === 'tshirt') {
      sizeSelect =
        document.createElement(
          'select'
        );

      sizeSelect.className =
        'bslabs-gift-option__size';

      sizeSelect.setAttribute(
        'aria-label',
        'Seleziona la taglia della T-shirt'
      );

      for (
        const variant of
        availableVariants
      ) {
        const option =
          document.createElement(
            'option'
          );

        option.value =
          String(variant.id);

        option.textContent =
          variant.size ||
          variant.title ||
          'Taglia';

        if (
          currentGiftLine &&
          Number(
            currentGiftLine
              .variant_id
          ) ===
          Number(variant.id)
        ) {
          option.selected = true;
        }

        sizeSelect.appendChild(
          option
        );
      }

      controls.appendChild(
        sizeSelect
      );
    }

    const button =
      document.createElement(
        'button'
      );

    button.type = 'button';

    button.className =
      'bslabs-gift-option__button';

    if (
      availableVariants.length === 0
    ) {
      button.textContent =
        'Esaurito';

      button.disabled = true;
    } else if (
      isSelected &&
      giftType !== 'tshirt'
    ) {
      button.textContent =
        'Selezionato';

      button.disabled = true;
    } else if (isSelected) {
      button.textContent =
        'Aggiorna';
    } else {
      button.textContent =
        'Scegli';
    }

    button.addEventListener(
      'click',
      async () => {
        const selectedVariantId =
          giftType === 'tshirt'
            ? Number(
                sizeSelect?.value
              )
            : Number(
                availableVariants[0]
                  ?.id
              );

        if (!selectedVariantId) {
          setGiftMessage(
            'Nessuna variante disponibile.',
            'error'
          );

          return;
        }

        await chooseGift(
          giftType,
          selectedVariantId,
          tier
        );
      }
    );

    controls.appendChild(button);
    row.appendChild(name);
    row.appendChild(controls);

    return row;
  }


  function renderGiftSelector(
    cart,
    tier
  ) {
    const {
      selector,
      subtitle,
      options
    } = getGiftElements();

    if (
      !selector ||
      !subtitle ||
      !options
    ) {
      return;
    }

    selector.hidden = false;

    subtitle.textContent =
      getTierDescription(tier);

    options.innerHTML = '';

    const giftLines =
      getGiftLines(cart);

    const currentGiftLine =
      giftLines[0] || null;

    for (
      const giftType of
      tier.allowedGifts
    ) {
      options.appendChild(
        createGiftOption(
          giftType,
          currentGiftLine,
          tier
        )
      );
    }

    if (currentGiftLine) {
      const selectedType =
        getGiftTypeByVariantId(
          currentGiftLine
            .variant_id
        );

      const selectedGift =
        config.gifts[selectedType];

      setGiftMessage(
        `${
          selectedGift?.title ||
          'Omaggio'
        } selezionato.`,
        'success'
      );

      return;
    }

    if (tier.level === 100) {
      setGiftMessage(
        'Aggiunta dello Shaker in corso...'
      );

      return;
    }

    setGiftMessage(
      'Seleziona un solo omaggio.'
    );
  }
  async function chooseGift(
    giftType,
    variantId,
    requestedTier
  ) {
    if (giftOperationInProgress) return;

    giftOperationInProgress = true;
    setGiftButtonsLoading(true);
    setGiftMessage(
      'Aggiornamento dell’omaggio...'
    );

    try {
      let cart = await readCart();

      const synchronized =
        await synchronizeAthleteCodeAttribute(
          cart
        );

      cart = synchronized.cart;

      const activeCode =
        synchronized.activeCode;

      const subtotal =
        getQualifyingSubtotal(
          cart,
          activeCode
        );

      const currentTier =
        activeCode
          ? getGiftTier(subtotal)
          : null;

      if (
        !activeCode ||
        !currentTier ||
        !currentTier.allowedGifts.includes(
          giftType
        )
      ) {
        throw new Error(
          'La promozione non è più valida.'
        );
      }

      if (
        Number(requestedTier.level) !==
        Number(currentTier.level)
      ) {
        throw new Error(
          'Il totale del carrello è cambiato.'
        );
      }

      await removeAllGiftLines(cart);
      await wait(150);

      await addGiftVariant(
        variantId,
        currentTier.level
      );

      setGiftMessage(
        'Omaggio aggiunto correttamente.',
        'success'
      );

      reloadCartPage(350);
    } catch (error) {
      console.error(
        'BSLABS: errore selezione omaggio',
        error
      );

      setGiftMessage(
        error.message ||
        'Non è stato possibile aggiungere l’omaggio.',
        'error'
      );

      setGiftButtonsLoading(false);
      giftOperationInProgress = false;
    }
  }

  async function syncGifts() {
    if (promoSyncInProgress) return;

    promoSyncInProgress = true;

    try {
      let cart = await readCart();

      const synchronized =
        await synchronizeAthleteCodeAttribute(
          cart
        );

      cart = synchronized.cart;
      const activeCode =
        synchronized.activeCode;

      if (activeCode) {
        displayActiveAthleteCode(activeCode);
      } else {
        displayNoAthleteCode();
        setAthleteMessage('');
      }

      const subtotal =
        getQualifyingSubtotal(
          cart,
          activeCode
        );

      const tier =
        activeCode
          ? getGiftTier(subtotal)
          : null;

      console.log(
        'BSLABS: subtotale netto valido',
        (subtotal / 100).toFixed(2) + ' €'
      );

      console.log(
        'BSLABS: fascia omaggio',
        tier || 'Nessuna'
      );

      const cartWasChanged =
        await normalizeGiftLines(
          cart,
          activeCode,
          tier
        );

      if (cartWasChanged) {
        reloadCartPage(300);
        return;
      }

      if (!activeCode || !tier) {
        hideGiftSelector();
        return;
      }

      const refreshedCart =
        await readCart();

      renderGiftSelector(
        refreshedCart,
        tier
      );
    } catch (error) {
      console.error(
        'BSLABS: errore sincronizzazione omaggi',
        error
      );

      setGiftMessage(
        'Non è stato possibile aggiornare gli omaggi.',
        'error'
      );
    } finally {
      promoSyncInProgress = false;
    }
  }

  async function updateAcceleratedCheckoutButtons() {
    try {
      const cart = await readCart();

      const hasAthleteCode =
        Boolean(
          getAppliedAthleteCode(cart)
        );

      document
        .querySelectorAll(
          '.additional-checkout-buttons, .cart__dynamic-checkout-buttons'
        )
        .forEach((container) => {
          container.hidden = hasAthleteCode;
        });
    } catch (error) {
      console.error(
        'BSLABS: errore pulsanti checkout rapido',
        error
      );
    }
  }


  async function initializePromoInterface() {
    const {
      container,
      input,
      applyButton,
      removeButton,
      message
    } = getAthleteElements();

    if (
      !container ||
      !input ||
      !applyButton ||
      !removeButton ||
      !message
    ) {
      return;
    }

    if (container.dataset.bslabsBound !== 'true') {
      applyButton.addEventListener(
        'click',
        applyAthleteCode
      );

      removeButton.addEventListener(
        'click',
        removeAthleteCode
      );

      input.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          applyAthleteCode();
        }
      );

      container.dataset.bslabsBound = 'true';
    }

    try {
      await syncGifts();
      await updateAcceleratedCheckoutButtons();
    } catch (error) {
      console.error(
        'BSLABS: errore inizializzazione',
        error
      );
    }
  }
  function schedulePromoRefresh(
    delay = 600
  ) {
    window.clearTimeout(
      promoRefreshTimer
    );

    promoRefreshTimer =
      window.setTimeout(
        initializePromoInterface,
        delay
      );
  }

  /*
   * Controlla automaticamente il carrello
   * dopo aggiunte, rimozioni e variazioni
   * delle quantità.
   */
  window.fetch = async (
    ...fetchArguments
  ) => {
    const response =
      await nativeFetch(
        ...fetchArguments
      );

    try {
      const requestTarget =
        fetchArguments[0]
          instanceof Request
          ? fetchArguments[0].url
          : String(
              fetchArguments[0] ||
              ''
            );

      const requestUrl =
        new URL(
          requestTarget,
          window.location.origin
        );

      const isCartMutation =
        /\/cart\/(add|change|update|clear)(\.js)?$/
          .test(
            requestUrl.pathname
          );

      if (isCartMutation) {
        schedulePromoRefresh(
          650
        );
      }
    } catch (error) {
      /*
       * Non blocca la richiesta
       * originale.
       */
    }

    return response;
  };

  document.addEventListener(
    'change',
    (event) => {
      const target =
        event.target;

      if (
        !(target instanceof Element)
      ) {
        return;
      }

      const isQuantityControl =
        target.matches(
          'input[name="updates[]"], quantity-input input, .quantity__input'
        );

      if (isQuantityControl) {
        schedulePromoRefresh(
          800
        );
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target;

      if (
        !(target instanceof Element)
      ) {
        return;
      }

      const cartControl =
        target.closest(
          'cart-remove-button, .quantity__button, [data-cart-remove], a[href*="/cart/change"]'
        );

      if (cartControl) {
        schedulePromoRefresh(
          900
        );
      }
    },
    true
  );


  async function validateBeforeCheckout() {
    let cart = await readCart();

    const synchronized =
      await synchronizeAthleteCodeAttribute(
        cart
      );

    cart = synchronized.cart;
    const activeCode =
      synchronized.activeCode;

    const subtotal =
      getQualifyingSubtotal(
        cart,
        activeCode
      );

    const tier =
      activeCode
        ? getGiftTier(subtotal)
        : null;

    const cartWasChanged =
      await normalizeGiftLines(
        cart,
        activeCode,
        tier
      );

    if (cartWasChanged) {
      return {
        valid: false,
        cartWasChanged: true,
        message:
          'Il carrello è stato aggiornato.'
      };
    }

    const refreshedCart =
      await readCart();

    const giftLines =
      getGiftLines(refreshedCart);

    if (
      activeCode &&
      tier &&
      tier.level >= 130 &&
      giftLines.length === 0
    ) {
      return {
        valid: false,
        cartWasChanged: false,
        message:
          'Seleziona il tuo omaggio prima di procedere.'
      };
    }

    return {
      valid: true,
      cartWasChanged: false,
      message: ''
    };
  }

  async function handleCheckoutAttempt(
    event,
    checkoutButton
  ) {
    if (checkoutInProgress) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    checkoutInProgress = true;

    const originalButtonText =
      checkoutButton.textContent;

    checkoutButton.disabled = true;

    checkoutButton.textContent =
      'Verifica del carrello...';

    try {
      const validation =
        await validateBeforeCheckout();

      if (
        validation.cartWasChanged
      ) {
        setGiftMessage(
          'Il carrello è stato corretto. Attendi...',
          'success'
        );

        reloadCartPage(300);

        return;
      }

      if (!validation.valid) {
        checkoutButton.disabled =
          false;

        checkoutButton.textContent =
          originalButtonText;

        checkoutInProgress =
          false;

        setGiftMessage(
          validation.message,
          'error'
        );

        document
          .getElementById(
            'BslabsGiftSelector'
          )
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

        return;
      }

      window.location.assign(
        `${root}checkout`
      );
    } catch (error) {
      console.error(
        'BSLABS: errore controllo checkout',
        error
      );

      checkoutButton.disabled =
        false;

      checkoutButton.textContent =
        originalButtonText;

      checkoutInProgress = false;

      setGiftMessage(
        'Controlla il carrello prima di procedere.',
        'error'
      );
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target;

      if (
        !(target instanceof Element)
      ) {
        return;
      }

      const checkoutButton =
        target.closest(
          'button[name="checkout"], #checkout'
        );

      if (checkoutButton) {
        handleCheckoutAttempt(
          event,
          checkoutButton
        );
      }
    },
    true
  );

  window.addEventListener(
    'pageshow',
    () => {
      schedulePromoRefresh(
        250
      );
    }
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      if (
        document.visibilityState ===
        'visible'
      ) {
        schedulePromoRefresh(
          300
        );
      }
    }
  );

  window.BSLABS_SYNC_GIFTS =
    initializePromoInterface;

  initializePromoInterface();
})();

